import type React from 'react';
import type { ComponentType, PropsWithChildren } from 'react';

import { TrackingContext } from '@change-corgi/core-react/tracking';

import { SetPageStateValue } from 'src/shared/pageState';

import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { StartAPetitionPageCombinedState, StartAPetitionPagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { StartAPetitionPageDataContextProvider } from './pageData';
import { StartAPetitionPageStateProvider } from './pageState';

export type ErrorHandlerProps = { context: ErrorStateDetails<StartAPetitionPageCombinedState> };

type Props = {
	prefetchedData: StartAPetitionPagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	redirectHandler: ComponentType<PropsWithChildren>;
	loadingFallback: JSX.Element;
};

export const StartAPetitionPageContextProvider: React.FC<PropsWithChildren<Props>> = ({
	children,
	prefetchedData,
	errorHandler: ErrorHandler,
	redirectHandler: RedirectHandler,
	loadingFallback,
}) => {
	const pageContextState = usePageContext(prefetchedData);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData } = pageContextState;

	const { initialLoginState } = pageData.data.draft.metadata?.user ?? {};
	const { variant } = pageData.data.draft.metadata ?? {};

	return (
		<StartAPetitionPageDataContextProvider pageData={pageData}>
			<TrackingContext properties={{ initial_login_state: initialLoginState }}>
				<RedirectHandler>
					<StartAPetitionPageStateProvider>
						<SetPageStateValue name="draft" value={{ variant }} />
						{children}
					</StartAPetitionPageStateProvider>
				</RedirectHandler>
			</TrackingContext>
		</StartAPetitionPageDataContextProvider>
	);
};
